import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import {
	Confirm,
	Icons,
	NotesModal,
	PageTitle,
	Table,
	UnavailableFeatureMessage,
} from '@components/common';
import FlatTreeXML from '@components/common/FlatXMLTree/FlatTreeXML';
import Modal from '@components/common/Modal';

import useMutateConfigurationTemplateDelete
	from '@hooks/cmModule/configurationTemplates/useMutateConfigurationTemplateDelete';
import useMutateConfigurationTemplatesCheckName
	from '@hooks/cmModule/configurationTemplates/useMutateConfigurationTemplatesCheckName';
import useMutateConfigurationTemplateUpdateNotes
	from '@hooks/cmModule/configurationTemplates/useMutateConfigurationTemplateUpdateNotes';
import useQueryConfigurationTemplate from '@hooks/cmModule/configurationTemplates/useQueryConfigurationTemplate';
import useQueryConfigurationTemplates from '@hooks/cmModule/configurationTemplates/useQueryConfigurationTemplates';
import useQueryConfigurationTemplatesAssignedStatuses from '@hooks/cmModule/configurationTemplates/useQueryConfigurationTemplatesAssignedStatuses';
import useQueryConfigurationTemplatesConfigTypesOptions from '@hooks/cmModule/configurationTemplates/useQueryConfigurationTemplatesConfigTypesOptions';
import useQueryConfigurationTemplatesModelCollections from '@hooks/cmModule/configurationTemplates/useQueryConfigurationTemplatesModelCollections';
import useQueryConfigurationTemplatesUsers from '@hooks/cmModule/configurationTemplates/useQueryConfigurationTemplatesUsers';
import { useVendorDrivenFeatureAvailability } from '@hooks/utils/useVendorDrivenFeatureAvailability';

import { auth, configurationManagement } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { createMoTreeFromFlatMavenirTree } from '@utils/treeFormatter/treeFormatter';
import { convertXMLToFlatTreeData } from '@components/common/FlatXMLTree/utils';

import { IConfigurationTemplate } from '@interfaces/cmModule/configurationTemplates';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITablePagination, ITableSearch, ITableSort, TTableFilter } from '@interfaces/components/table';
import { IFlatMoTreeNode } from '@interfaces/components/tree';
import { TMapTree } from '@components/common/FlatXMLTree/interface';

import { defaultMeta } from '@enums/common';

import { MIN_FILTER_WIDTH } from '@constants/common';
import { MODAL_WIDTH_580, MODAL_WIDTH_1000 } from '@constants/modals';
import { routes } from '@constants/routes';
import { TableFilterTypes } from '@constants/table';
import {
	NON_EDITABLE_CONFIGURATION_IDS,
} from '@components/routes/AdminPanel/ManageNetworkElementConfigurations/constants';

import ConfigurationTemplateForm from './ConfigurationTemplateForm';
import { convertMoTreeToXmlData, getConfigurationTemplateColumns } from './utils';

import styles from './ConfigurationTemplates.module.scss';

const ConfigurationTemplates = () => {
	const {
		accessPermission: {
			networkOperatorCmModuleAdmin,
			networkOperatorCmModuleEditor,
		},
	} = useAppSelector(auth);

	const navigate = useNavigate();

	const { selectedVendor } = useAppSelector(configurationManagement);

	const [selectedTemplate, setSelectedTemplate] = useState<IConfigurationTemplate | undefined>(undefined);

	const [isTemplateModal, setIsTemplateModal] = useState(false);
	const [isDeleteTemplateConfirm, setIsDeleteTemplateConfirm] = useState(false);
	const [isViewTreeModal, setIsViewModal] = useState(false);
	const [isNotesModal, setIsNotesModal] = useState(false);

	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isDirtyNotesModal, setIsDirtyNotesModal] = useState(false);
	const [isCloneMode, setIsCloneMode] = useState(false);

	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [searchValue, setSearchValue] = useState('');
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);

	const [selectedAssignedStatus, setSelectedAssignedStatus] = useState<TSelectOption<string>>(null);
	const [selectedConfigType, setSelectedConfigType] = useState<TSelectOption<string>>(null);
	const [selectedUser, setSelectedUser] = useState<TSelectOption<string>>(null);
	const [selectedYangModelCollection, setSelectedYangModelCollection] = useState<TSelectOption<string>>(null);

	const [treeData, setTreeData] = useState<TMapTree>(new Map());

	const {
		configTemplates,
		countOfRecords,
		isLoadingConfigTemplates,
	} = useQueryConfigurationTemplates({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		assignedStatus: selectedAssignedStatus?.value,
		configType: selectedConfigType?.value,
		userId: selectedUser?.value,
		validationRulesFolderId: selectedYangModelCollection?.value,
	});

	const {
		configTypesOptions,
		isLoadingConfigTypesOptions,
	} = useQueryConfigurationTemplatesConfigTypesOptions();

	const {
		assignedStatuses,
		isLoadingAssignedStatuses,
	} = useQueryConfigurationTemplatesAssignedStatuses();

	const {
		configurationTemplateUsers,
		isLoadingConfigurationTemplateUsers,
	} = useQueryConfigurationTemplatesUsers();

	const {
		configurationTemplateModelCollections,
		isLoadingConfigurationTemplateModelCollections,
	} = useQueryConfigurationTemplatesModelCollections();

	const isViewerOnly = !networkOperatorCmModuleEditor && !networkOperatorCmModuleAdmin;
	const isFeatureAvailable = useVendorDrivenFeatureAvailability();

	const handleConfigTypeChange = useCallback((option: TSelectOption<string>) => {
		setSelectedConfigType(option);
		setPage(defaultMeta.page);
	}, []);

	const handleAssignedStatusChange = useCallback((option: TSelectOption<string>) => {
		setSelectedAssignedStatus(option);
		setPage(defaultMeta.page);
	}, []);

	const handleUserChange = useCallback((option: TSelectOption<string>) => {
		setSelectedUser(option);
		setPage(defaultMeta.page);
	}, []);

	const handleYangModelCollectionChange = useCallback((option: TSelectOption<string>) => {
		setSelectedYangModelCollection(option);
		setPage(defaultMeta.page);
	}, []);

	const filters: TTableFilter[] = useMemo(() => {
		return [
			{
				name: 'configType',
				placeholder: 'Config type',
				option: selectedConfigType,
				options: configTypesOptions,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleConfigTypeChange,
				defaultValue: null,
			},
			{
				name: 'validationRulesFolderId',
				placeholder: 'YANG Model Collections',
				option: selectedYangModelCollection,
				options: configurationTemplateModelCollections,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleYangModelCollectionChange,
				defaultValue: null,
			},
			{
				name: 'assignedStatus',
				placeholder: 'Consistency check',
				option: selectedAssignedStatus,
				options: assignedStatuses,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleAssignedStatusChange,
				defaultValue: null,
			},
			{
				name: 'userId',
				placeholder: 'User',
				option: selectedUser,
				options: configurationTemplateUsers,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleUserChange,
				defaultValue: null,
			},
		];
	}, [
		selectedConfigType,
		selectedYangModelCollection,
		selectedAssignedStatus,
		selectedUser,
		configTypesOptions,
		configurationTemplateModelCollections,
		assignedStatuses,
		configurationTemplateUsers,
		handleConfigTypeChange,
		handleYangModelCollectionChange,
		handleAssignedStatusChange,
		handleUserChange,
	]);

	const openDeleteTemplateModal = useCallback((template: IConfigurationTemplate) => {
		setSelectedTemplate(template);
		setIsDeleteTemplateConfirm(true);
	}, []);

	const closeDeleteTemplateModal = useCallback(() => {
		setSelectedTemplate(undefined);
		setIsDeleteTemplateConfirm(false);
	}, []);

	const onTreeParseSuccess = (data: IFlatMoTreeNode[]) => {
		const moTree = createMoTreeFromFlatMavenirTree(data);

		if (!moTree) {
			return;
		}

		const flatTreeXmlData = convertXMLToFlatTreeData(
			convertMoTreeToXmlData(moTree),
			NON_EDITABLE_CONFIGURATION_IDS,
		);

		setTreeData(flatTreeXmlData);
	};

	const {
		isLoadingConfigTemplate,
	} = useQueryConfigurationTemplate({ id: selectedTemplate?.id, onSuccess: onTreeParseSuccess });

	const {
		updateNotes,
		isLoadingUpdateNotes,
	} = useMutateConfigurationTemplateUpdateNotes();

	const {
		deleteTemplate,
		isLoadingDeleteTemplate,
	} = useMutateConfigurationTemplateDelete({
		onSuccess: closeDeleteTemplateModal,
	});

	const {
		checkTemplateNameError,
		checkTemplateName,
		resetCheckTemplateName,
	} = useMutateConfigurationTemplatesCheckName();

	const handleUpdateNotes = useCallback(async (notes: string) => {
		if (!selectedTemplate) {
			return;
		}

		await updateNotes({
			id: selectedTemplate?.id,
			notes,
		});
	}, [selectedTemplate, updateNotes]);

	const openTemplateModal = useCallback((
		template?: IConfigurationTemplate,
		isCloneMode?: boolean,
	) => {
		if (template) {
			setSelectedTemplate(template);
		}

		setIsTemplateModal(true);
		setIsCloneMode(!!isCloneMode);
	}, []);

	const closeTemplateModal = useCallback(() => {
		setSelectedTemplate(undefined);
		setIsTemplateModal(false);
		setIsCloneMode(false);
		resetCheckTemplateName();
	}, [resetCheckTemplateName]);

	const openNotesModal = useCallback(() => {
		setIsNotesModal(true);
	}, []);

	const closeNotesModal = useCallback(() => {
		setIsNotesModal(false);
		setIsDirtyNotesModal(false);
		setSelectedTemplate(undefined);
	}, [setSelectedTemplate]);

	const openViewTreeModal = useCallback((template: IConfigurationTemplate) => {
		setSelectedTemplate(template);
		setIsViewModal(true);
	}, []);

	const closeViewTreeModal = useCallback(() => {
		setSelectedTemplate(undefined);
		setIsViewModal(false);
	}, []);

	const deleteTemplateHandler = useCallback(() => {
		if (!selectedTemplate) {
			return;
		}

		deleteTemplate({ id: selectedTemplate.id });
		setSelectedTemplate(undefined);
	}, [deleteTemplate, selectedTemplate]);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
		setPage(defaultMeta.page);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const search: ITableSearch[] = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: 'Search by name',
	}], [searchValue, searchHandler]);

	const openConsistencyCheck = useCallback((id: string) => {
		navigate(`${routes.adminPanel.index}${routes.cmModule.consistencyCheck}?templateId=${id}`);
	}, [navigate]);

	const columns = useMemo(() => getConfigurationTemplateColumns({
		setSelectedTemplate,
		openNotesModal,
		openViewTreeModal,
		openTemplateModal,
		openDeleteTemplateModal,
		openConsistencyCheck,
		isViewerOnly,
	}), [
		openConsistencyCheck,
		openNotesModal,
		openTemplateModal,
		openViewTreeModal,
		openDeleteTemplateModal,
		isViewerOnly,
	]);

	const isLoading = isLoadingConfigTemplates ||
		isLoadingConfigTypesOptions ||
		isLoadingAssignedStatuses ||
		isLoadingConfigurationTemplateUsers ||
		isLoadingConfigurationTemplateModelCollections;

	return isFeatureAvailable ? (
		<div className={styles.wrapper}>
			<PageTitle
				icon={Icons.configurationIcon()}
				title='Configuration Templates'
			/>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyNotesModal}
				isOpen={isNotesModal}
				onClose={closeNotesModal}
				title='Notes'
				width={MODAL_WIDTH_580}
			>
				<NotesModal
					isLoading={isLoadingUpdateNotes}
					isReadOnly
					name={selectedTemplate?.name || ''}
					notes={selectedTemplate?.notes || ''}
					onSubmit={handleUpdateNotes}
					setIsDirtyForm={setIsDirtyNotesModal}
				/>
			</Modal>
			<Modal
				isOpen={isDeleteTemplateConfirm}
				onClose={closeDeleteTemplateModal}
			>
				<Confirm
					isLoading={isLoadingDeleteTemplate}
					onClose={closeDeleteTemplateModal}
					onSubmit={deleteTemplateHandler}
					question='Are you sure you want to delete the template?'
				/>
			</Modal>
			<Modal
				isOpen={isViewTreeModal}
				onClose={closeViewTreeModal}
				title='View configuration template'
				width={MODAL_WIDTH_1000}
			>
				<div className={styles.treeFrame}>
					<FlatTreeXML
						data={treeData}
						errors={undefined}
						isLoading={isLoadingConfigTemplate}
						isReadOnly
						isWithAdd
						isWithCopy
						isWithSkip
						setData={setTreeData}
					/>
				</div>
			</Modal>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isTemplateModal}
				onClose={closeTemplateModal}
				title='Add new template'
				width={MODAL_WIDTH_580}
			>
				<ConfigurationTemplateForm
					checkTemplateName={checkTemplateName}
					checkTemplateNameError={checkTemplateNameError}
					closeTemplateModal={closeTemplateModal}
					isCloneMode={isCloneMode}
					isEditMode={!!selectedTemplate && !isCloneMode}
					setIsDirtyForm={setIsDirtyFormModal}
					template={selectedTemplate}
				/>
			</Modal>
			<div className={styles.tableContainer}>
				<Table
					addTooltipText='Add new template'
					columns={columns}
					data={configTemplates}
					filters={filters}
					isCreatable={!isViewerOnly}
					isLoading={isLoading}
					isPinColumns
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					search={search}
					sort={sort}
					stickyLeftColumnIndexes={[0, 1]}
					toggleAddModal={openTemplateModal}
				/>
			</div>
		</div>
	) : <UnavailableFeatureMessage vendorName={selectedVendor?.value} />;
};

export default ConfigurationTemplates;
